import React, { FunctionComponent } from "react";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import {
  useComponentsRequests,
  useRetryComponentRequest
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import {
  ComponentRequestDto,
  ComponentRequestStatuses,
  GetComponentsRequestsArgs
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  confirm,
  IColumn,
  IconButton,
  MessageBar,
  MessageBarType,
  ScrollablePane,
  Stack
} from "@bps/fluent-ui";
import { CopyableGuid } from "@components/CopyableText";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";
import { ExpandableRow } from "@components/tables/ExpandableRow";
import { useExpandedRows } from "@components/tables/useExpandedRows";
import { ComponentText } from "../../components/components/ComponentText";
import { ComponentsRequestsDetails } from "./ComponentsRequestsDetails";
import {
  ManagerWorkflowRunInstanceLink,
  ComponentDateText
} from "../../_shared-blocks";

interface ComponentsRequestsListProps {
  filter: GetComponentsRequestsArgs;
}

export const ComponentsRequestsList: FunctionComponent<ComponentsRequestsListProps> = ({
  filter
}) => {
  const { expandedIds, toggleExpandedIds } = useExpandedRows();

  const { data: hasWritePermission } = usePermissionsCheckQuery([
    Permissions.PltComponentsWrite
  ]);

  const { isLoading, error, data = [] } = useComponentsRequests(filter, {
    refetchOnMount: "always"
  });

  const {
    mutate: retryComponentRequest,
    isLoading: retryIsLoading,
    error: retryError,
    reset: resetRetry
  } = useRetryComponentRequest();

  const retryErrorMessage = (retryError?.detail || retryError?.message) as
    | string
    | undefined;

  const confirmRetry = (code: string) =>
    confirm({
      dialogContentProps: {
        title: "Retry component request",
        subText: `Are you sure you want to retry the request for ${code}?`
      }
    });

  const columns: IColumn[] = [
    {
      key: "actions",
      name: "",
      minWidth: 32,
      maxWidth: 32,
      onRender: (item: ComponentRequestDto) => (
        <IconButton
          disabled={!hasWritePermission || retryIsLoading}
          styles={{ root: { justifySelf: "end" } }}
          menuIconProps={{ iconName: "more" }}
          menuProps={{
            items: [
              {
                key: "retry",
                text: "Retry",
                disabled: item.status !== ComponentRequestStatuses.Failed,
                onClick: () => {
                  confirmRetry(item.code).then(isConfirmed => {
                    if (isConfirmed) {
                      retryComponentRequest(item.id);
                    }
                  });
                }
              }
            ]
          }}
        />
      )
    },
    {
      key: "id",
      name: "ID",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) => (
        <CopyableGuid value={item.id} short />
      )
    },
    {
      key: "code",
      name: "Code",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) => (
        <Stack>
          <ComponentText code={item.code} />
          {retryErrorMessage && (
            <MessageBar
              onDismiss={ev => {
                ev?.stopPropagation();
                resetRetry();
              }}
              messageBarType={MessageBarType.error}
              isMultiline
              styles={{
                root: { whiteSpace: "normal", wordBreak: "break-word" }
              }}
            >
              {retryErrorMessage}
            </MessageBar>
          )}
        </Stack>
      )
    },
    {
      key: "scope-id",
      name: "Scope ID",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) => (
        <CopyableGuid value={item.scopeId} short />
      )
    },
    {
      key: "action",
      name: "Action",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) => item.action
    },
    {
      key: "status",
      name: "Status",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) => item.status
    },
    {
      key: "instance-id",
      name: "WF Instance Link",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) =>
        item.componentManagerWorkflowRunInstanceId ? (
          <ManagerWorkflowRunInstanceLink
            id={item.componentManagerWorkflowRunInstanceId}
          />
        ) : (
          "-"
        )
    },
    {
      key: "data",
      name: "Date",
      minWidth: 100,
      maxWidth: 100,
      onRender: (item: ComponentRequestDto) => (
        <ComponentDateText changeLog={item.changeLog} />
      )
    }
  ];
  return (
    <Stack styles={{ root: { position: "relative", height: "100%" } }}>
      <ScrollablePane>
        <ShimmeredDetailsList
          enableShimmer={isLoading}
          errorMessage={error?.message}
          items={data}
          columns={columns}
          stickyHeader
          onRenderRow={(props, renderDefault) => {
            if (props && renderDefault) {
              return (
                <ExpandableRow
                  expandedIds={expandedIds}
                  toggleExpandedIds={toggleExpandedIds}
                  id={props.item.id}
                  ExpandableComponent={
                    <ComponentsRequestsDetails componentsRequest={props.item} />
                  }
                  rowProps={props}
                />
              );
            }
            return null;
          }}
        />
      </ScrollablePane>
    </Stack>
  );
};
